import React from 'react';
import { MapPin, Clock, AlertTriangle, Target, Factory, Zap, Terminal, ExternalLink, ShieldAlert } from 'lucide-react';
import { ThreatActor } from '@/mock/threatActors';

interface DescriptionPanelProps {
    actor: ThreatActor;
}

const DescriptionPanel: React.FC<DescriptionPanelProps> = ({ actor }) => {
    const getSeverityStyles = (severity: string) => {
        switch (severity.toLowerCase()) {
            case 'critical': return 'border-[#FF3333]/40 text-[#FF3333] bg-[#FF3333]/5 shadow-[0_0_10px_rgba(255,51,51,0.2)]';
            case 'high': return 'border-[#F97316]/40 text-[#F97316] bg-[#F97316]/5 shadow-[0_0_10px_rgba(249,115,22,0.2)]';
            case 'medium': return 'border-[#EAB308]/40 text-[#EAB308] bg-[#EAB308]/5 shadow-[0_0_10px_rgba(234,179,8,0.2)]';
            default: return 'border-slate-500/40 text-slate-400 bg-slate-500/5';
        }
    };

    const getMitreUrl = (techId: string) => {
        if (techId.includes('.')) {
            const [base, sub] = techId.split('.');
            return `https://attack.mitre.org/techniques/${base}/${sub}/`;
        }
        return `https://attack.mitre.org/techniques/${techId}/`;
    };

    const cleanDescription = (desc: string) => {
        return desc.replace(/\(https?:\/\/[^\s)]+\)/g, '').replace(/\[\vert{}\]/g, '');
    };

    return (
        <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 font-mono shadow-[0_0_40px_rgba(0,0,0,0.5)] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#02DFC2]/40 to-transparent"></div>

            {/* Dossier Header */}
            <div className="mb-6 border-b border-[#1A1D24] pb-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-[10px] text-[#02DFC2] font-bold tracking-[0.2em] uppercase">
                            <Terminal className="w-3 h-3" /> Intelligence_Dossier
                        </div>
                        <h3 className="text-2xl font-bold text-white tracking-tight uppercase flex items-center gap-3">
                            {actor.name}
                        </h3>
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-6 px-4 py-2 bg-[#050505] border border-[#1A1D24] rounded-lg">
                            <div className="flex flex-col">
                                <span className="text-[9px] text-slate-500 uppercase tracking-widest">Registry_ID</span>
                                <span className="text-[12px] text-white font-bold uppercase">{actor.id}</span>
                            </div>
                            <div className="w-[1px] h-6 bg-[#1A1D24]"></div>
                            <div className="flex flex-col">
                                <span className="text-[9px] text-slate-500 uppercase tracking-widest">Origin_Point</span>
                                <div className="flex items-center gap-1 text-[12px] text-white font-bold uppercase">
                                    <MapPin className="w-3 h-3 text-[#02DFC2]" /> {actor.country}
                                </div>
                            </div>
                            <div className="w-[1px] h-6 bg-[#1A1D24]"></div>
                            <div className="flex flex-col">
                                <span className="text-[9px] text-slate-500 uppercase tracking-widest">Persistence</span>
                                <div className="flex items-center gap-1 text-[12px] text-white font-bold uppercase">
                                    <Clock className="w-3 h-3 text-[#02DFC2]" /> Since {actor.firstSeen}
                                </div>
                            </div>
                        </div>
                        <span className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] rounded border ${getSeverityStyles(actor.severity)}`}>
                            {actor.severity} threat_lvl
                        </span>
                    </div>
                </div>

                {/* Aliases Section */}
                {actor.aliases && actor.aliases.length > 0 && (
                    <div className="flex items-center gap-3">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex-shrink-0">
                            Known_Designations:
                        </span>
                        <div className="flex flex-wrap gap-2 max-h-[30px] overflow-hidden">
                            {actor.aliases.map((alias, index) => (
                                <span
                                    key={index}
                                    className="px-2 py-0.5 text-[10px] bg-[#12151C] text-slate-400 rounded border border-[#1A1D24] uppercase tracking-tighter truncate max-w-[150px]"
                                    title={alias}
                                >
                                    {alias}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Tactical Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {/* Overview */}
                <div className="space-y-4 flex flex-col h-full">
                    <h4 className="text-[11px] font-bold text-white uppercase tracking-[0.2em] flex items-center gap-2 flex-shrink-0">
                        <AlertTriangle className="w-4 h-4 text-orange-500" /> Overview
                    </h4>
                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 max-h-[140px]">
                        <p className="text-[12px] text-slate-400 leading-relaxed break-words border-l border-[#1A1D24] pl-4 italic">
                            "{cleanDescription(actor.description)}"
                        </p>
                    </div>
                </div>

                {/* Primary Objectives */}
                <div className="space-y-4 flex flex-col h-full">
                    <h4 className="text-[11px] font-bold text-white uppercase tracking-[0.2em] flex items-center gap-2 flex-shrink-0">
                        <Target className="w-4 h-4 text-[#FF3333]" /> Primary Objectives
                    </h4>
                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 max-h-[140px]">
                        {actor.objectives && actor.objectives.length > 0 ? (
                            <ul className="space-y-3">
                                {actor.objectives.map((objective, index) => (
                                    <li key={index} className="flex items-start gap-3 group">
                                        <div className="w-1.5 h-1.5 bg-[#FF3333] rounded-full mt-1.5 flex-shrink-0 shadow-[0_0_5px_#FF3333] group-hover:scale-125 transition-transform" />
                                        <span className="text-[11px] text-slate-300 uppercase leading-tight break-words group-hover:text-white transition-colors">
                                            {objective}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="flex items-center gap-2 text-[10px] text-slate-600 uppercase tracking-widest border border-[#1A1D24] bg-[#050505] p-3 rounded-lg">
                                <ShieldAlert className="w-3 h-3" /> Data classifying...
                            </div>
                        )}
                    </div>
                </div>

                {/* Target Industries */}
                <div className="space-y-4 flex flex-col h-full">
                    <h4 className="text-[11px] font-bold text-white uppercase tracking-[0.2em] flex items-center gap-2 flex-shrink-0">
                        <Factory className="w-4 h-4 text-[#50A970]" /> Target Sectors
                    </h4>
                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 max-h-[140px]">
                        {actor.industries && actor.industries.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {actor.industries.map((industry, index) => (
                                    <span
                                        key={index}
                                        className="px-2.5 py-1 text-[10px] font-bold bg-[#50A970]/10 text-[#50A970] rounded border border-[#50A970]/30 uppercase tracking-widest break-words"
                                    >
                                        {industry}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-[10px] text-slate-600 uppercase tracking-widest border border-[#1A1D24] bg-[#050505] p-3 rounded-lg">
                                <ShieldAlert className="w-3 h-3" /> Vectors unknown
                            </div>
                        )}
                    </div>
                </div>

                {/* Common Techniques */}
                <div className="space-y-4 flex flex-col h-full">
                    <h4 className="text-[11px] font-bold text-white uppercase tracking-[0.2em] flex items-center gap-2 flex-shrink-0">
                        <Zap className="w-4 h-4 text-[#EAB308]" /> Known Techniques
                    </h4>
                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 max-h-[140px]">
                        {actor.techniques && actor.techniques.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2">
                                {actor.techniques.map((technique, index) => {
                                    // TypeScript safe-cast to handle both old string format and new object format
                                    const tech = technique as unknown as { id: string; name: string } | string;
                                    const techId = typeof tech === 'string' ? tech : tech.id;
                                    const techName = typeof tech === 'string' ? tech : tech.name;

                                    return (
                                        <a
                                            key={index}
                                            href={getMitreUrl(techId)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center justify-between gap-2 px-2 py-1.5 bg-[#12151C] border border-[#1A1D24] rounded hover:border-[#EAB308]/50 hover:bg-[#EAB308]/5 transition-all group cursor-pointer overflow-hidden"
                                            title={`View ${techId} on MITRE ATT&CK`}
                                        >
                                            <span className="text-[10px] text-slate-400 group-hover:text-[#EAB308] font-bold tracking-wider truncate w-full text-left">
                                                {techName}
                                            </span>
                                            <ExternalLink className="w-3 h-3 text-slate-600 group-hover:text-[#EAB308] flex-shrink-0" />
                                        </a>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-[10px] text-slate-600 uppercase tracking-widest border border-[#1A1D24] bg-[#050505] p-3 rounded-lg">
                                <ShieldAlert className="w-3 h-3" /> No TTPs mapped
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DescriptionPanel;